import { ScreenContext } from '@hb/react'
import { SignableDocument, TemplateAttachment, WithId } from '@hb/shared'
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react'

export interface PdfViewContextValue {
  scrollPageIndex: number
  onScrollPage: (index: number) => void
  numPages: number | null
  updateNumPages: (num: number | null) => void
  attachments: Record<string, TemplateAttachment>
  document?: WithId<SignableDocument>
  editable: boolean
  pageSizes: Record<number, [number, number]>
  updatePageSize: (page: number, size: [number, number]) => void
  viewWidth: number
  onAfterRegenerate?: () => void
  modalWidth: number
}

const PdfViewContext = createContext<PdfViewContextValue>({
  scrollPageIndex: 0,
  attachments: {},
  onScrollPage: () => {},
  numPages: null,
  updateNumPages: () => {},
  editable: false,
  pageSizes: {},
  updatePageSize: () => {},
  viewWidth: 0,
  modalWidth: 0,
})

export const PdfViewProvider = ({
  children,
  attachments,
  document,
  editable,
  width,
  onAfterRegenerate,
  modalWidth,
}: PropsWithChildren<{
  attachments?: Record<string, TemplateAttachment>
  document?: WithId<SignableDocument>
  editable?: boolean
  width: number
  onAfterRegenerate?: () => void
  modalWidth?: number
}>) => {
  const { isMobile } = useContext(ScreenContext)
  const [scrollPageIndex, setScrollPageIndex] = useState(0)
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageSizes, setPageSizes] = useState<Record<number, [number, number]>>([])
  const updatePageSize = useCallback((page: number, size: [number, number]) => {
    setPageSizes(prev => ({ ...prev, [page]: size }))
  }, [])

  const value = useMemo<PdfViewContextValue>(
    () => ({
      scrollPageIndex,
      onScrollPage: setScrollPageIndex,
      attachments: attachments ?? {},
      numPages,
      pageSizes,
      updatePageSize,
      document,
      editable: !!editable,
      updateNumPages: n => setNumPages(n),
      viewWidth: width - (isMobile ? 0 : 30),
      onAfterRegenerate,
      modalWidth: modalWidth ?? width,
    }),
    [
      scrollPageIndex,
      numPages,
      attachments,
      document,
      editable,
      pageSizes,
      updatePageSize,
      width,
      modalWidth,
      isMobile,
      onAfterRegenerate,
    ],
  )
  return <PdfViewContext.Provider value={value}>{children}</PdfViewContext.Provider>
}

export const usePdfView = () => useContext(PdfViewContext)
